import { c as _c } from "react/compiler-runtime";
import { type BigIntish, type ChainId, ChainUtils, type MarketId, MathLib } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, type InterfaceInputToken, parseApiAsset, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { Avatar } from '@morpho-org/ui-v6/client';
import { formatters, getMarketNameParts } from '@morpho-org/ui-v6/utils';
import { TruncatedTooltip } from '@repo/ui';
import { memo, useMemo } from 'react';
import { InternalLink } from '@/components/common/InternalLink';
import { getMarketUrl } from '@/helpers/routing';
import { getTimelockFunctionConfig } from '../utils';
import { HoverableStack, StyledVaultPendingTimelock } from '../VaultPendingTimelock.components';
import VaultPendingTimelockHeader from './VaultPendingTimelockHeader';
interface VaultPendingTimelockChangeCapProps {
  chainId: ChainId;
  collateralAsset: InterfaceInputToken | null;
  loanAsset: InterfaceInputToken;
  supplyCap: BigIntish;
  currentSupplyCap?: BigIntish;
  functionName: string;
  marketId: MarketId;
}
const VaultPendingTimelockChangeCap = t0 => {
  const $ = _c(48);
  const {
    chainId,
    collateralAsset,
    loanAsset,
    supplyCap,
    currentSupplyCap,
    functionName,
    marketId
  } = t0;
  let t1;
  if ($[0] !== collateralAsset || $[1] !== loanAsset) {
    t1 = getMarketNameParts({
      loanAsset,
      collateralAsset
    });
    $[0] = collateralAsset;
    $[1] = loanAsset;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    loanSymbol,
    collateralSymbol
  } = t1;
  const supplyCapBigInt = toBigInt(supplyCap);
  const isSupplyCapInfinite = supplyCapBigInt >= MathLib.RAY;
  let t2;
  if ($[3] !== collateralAsset) {
    t2 = isDefined(collateralAsset);
    $[3] = collateralAsset;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isIdleMarket = !t2;
  let t3;
  bb0: {
    if (isSupplyCapInfinite) {
      t3 = "Infinite";
      break bb0;
    }
    if (isIdleMarket) {
      let t4;
      if ($[5] !== loanAsset.decimals || $[6] !== loanAsset.symbol || $[7] !== supplyCapBigInt) {
        t4 = formatters.short.trailingZero(false).digits(2).unit(loanAsset.symbol).of(supplyCapBigInt, loanAsset.decimals);
        $[5] = loanAsset.decimals;
        $[6] = loanAsset.symbol;
        $[7] = supplyCapBigInt;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      t3 = t4;
      break bb0;
    }
    let t4;
    if ($[9] !== loanAsset || $[10] !== supplyCapBigInt) {
      const token = parseApiAsset(loanAsset);
      const supplyCapUsdValue = token.toUsd(supplyCapBigInt);
      t4 = formatters.short.trailingZero(false).digits(2).unit("$").default("-").of(supplyCapUsdValue, 18);
      $[9] = loanAsset;
      $[10] = supplyCapBigInt;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = t4;
  }
  const formattedSupplyCap = t3;
  let t4;
  if ($[12] !== currentSupplyCap) {
    t4 = toBigInt(currentSupplyCap) ?? ZERO;
    $[12] = currentSupplyCap;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const currentSupplyCapBigInt = t4;
  const isCurrentSupplyCapInfinite = currentSupplyCapBigInt >= MathLib.RAY;
  let t5;
  bb1: {
    if (isCurrentSupplyCapInfinite) {
      t5 = "Infinite";
      break bb1;
    }
    if (isIdleMarket) {
      let t6;
      if ($[14] !== currentSupplyCapBigInt || $[15] !== loanAsset.decimals || $[16] !== loanAsset.symbol) {
        t6 = formatters.short.trailingZero(false).digits(2).unit(loanAsset.symbol).of(currentSupplyCapBigInt, loanAsset.decimals);
        $[14] = currentSupplyCapBigInt;
        $[15] = loanAsset.decimals;
        $[16] = loanAsset.symbol;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      t5 = t6;
      break bb1;
    }
    let t6;
    if ($[18] !== currentSupplyCapBigInt || $[19] !== loanAsset) {
      const token_0 = parseApiAsset(loanAsset);
      const currentSupplyCapUsdValue = token_0.toUsd(currentSupplyCapBigInt);
      t6 = formatters.short.trailingZero(false).digits(2).unit("$").default("-").of(currentSupplyCapUsdValue, 18);
      $[18] = currentSupplyCapBigInt;
      $[19] = loanAsset;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    t5 = t6;
  }
  const formattedCurrentSupplyCap = t5;
  let t6;
  if ($[21] !== currentSupplyCap || $[22] !== functionName) {
    t6 = getTimelockFunctionConfig({
      functionName,
      hasPreviousValue: isDefined(currentSupplyCap)
    });
    $[21] = currentSupplyCap;
    $[22] = functionName;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const config = t6;
  let t7;
  bb2: {
    if (isIdleMarket) {
      t7 = !isDefined(currentSupplyCap) ? "Enable idle liquidity" : "Change idle liquidity cap";
      break bb2;
    }
    t7 = config?.label ?? "";
  }
  const label = t7;
  let t8;
  if ($[24] !== chainId || $[25] !== collateralAsset || $[26] !== collateralSymbol || $[27] !== config || $[28] !== isIdleMarket || $[29] !== label || $[30] !== loanAsset || $[31] !== loanSymbol || $[32] !== marketId) {
    t8 = config && <VaultPendingTimelockHeader title={label} criticality={config.criticality}>{isIdleMarket && <HoverableStack onClick={e => {
        e.stopPropagation();
        const url = ChainUtils.getExplorerAddressUrl(loanAsset.chain.id, loanAsset.address);
        window.open(url, "_blank");
      }}><div className="gap-xs flex min-w-[0px] items-center"><Avatar aria-hidden={true} src={loanAsset.logoURI} size="small" /><span className="text-2xs text-foreground">{loanSymbol}</span></div></HoverableStack>}{isDefined(marketId) && collateralAsset && <InternalLink className="min-w-0" href={getMarketUrl({
        identifier: InterfaceChainUtils.getIdentifierById(chainId),
        marketId,
        collateralSymbol: collateralAsset.symbol ?? "",
        loanSymbol: loanAsset.symbol ?? ""
      })} onClick={_temp}><HoverableStack><div className="gap-xs text-2xs text-foreground flex min-w-[0px] items-center"><span className="gap-xs inline-flex shrink-0 items-center"><Avatar aria-hidden={true} src={loanAsset.logoURI} size="small" /><TruncatedTooltip text={loanSymbol}>{ref => <span ref={ref} tabIndex={-1} className="min-w-0 truncate">{loanSymbol}</span>}</TruncatedTooltip></span><span aria-hidden={true} className="bg-muted-foreground h-sm w-px shrink-0 rounded-full" /><span className="sr-only">backed by</span><span className="gap-xs inline-flex min-w-0 items-center"><Avatar aria-hidden={true} src={collateralAsset.logoURI} size="small" /><TruncatedTooltip text={collateralSymbol ?? ""}>{ref_0 => <span ref={ref_0} tabIndex={-1} className="min-w-0 truncate">{collateralSymbol}</span>}</TruncatedTooltip></span></div></HoverableStack></InternalLink>}</VaultPendingTimelockHeader>;
    $[24] = chainId;
    $[25] = collateralAsset;
    $[26] = collateralSymbol;
    $[27] = config;
    $[28] = isIdleMarket;
    $[29] = label;
    $[30] = loanAsset;
    $[31] = loanSymbol;
    $[32] = marketId;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== currentSupplyCap || $[35] !== formattedCurrentSupplyCap) {
    t9 = isDefined(currentSupplyCap) && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">Initial value</span><span className="text-2xs text-foreground">{formattedCurrentSupplyCap}</span></StyledVaultPendingTimelock.Row>;
    $[34] = currentSupplyCap;
    $[35] = formattedCurrentSupplyCap;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  const t10 = !isDefined(currentSupplyCap) ? "New supply cap" : "Supply cap";
  let t11;
  if ($[37] !== t10) {
    t11 = <span className="text-2xs text-foreground">{t10}</span>;
    $[37] = t10;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== formattedSupplyCap) {
    t12 = <span className="text-2xs text-foreground">{formattedSupplyCap}</span>;
    $[39] = formattedSupplyCap;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t11 || $[42] !== t12) {
    t13 = <StyledVaultPendingTimelock.Row>{t11}{t12}</StyledVaultPendingTimelock.Row>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t13 || $[45] !== t8 || $[46] !== t9) {
    t14 = <div className="flex flex-col">{t8}{t9}{t13}</div>;
    $[44] = t13;
    $[45] = t8;
    $[46] = t9;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
};
export default memo(VaultPendingTimelockChangeCap);
function _temp(e_0) {
  return e_0.stopPropagation();
}